import { FormEvent, useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useAdminSession } from "@/auth/AdminSession";
import { openTenant } from "@/auth/tenantSelection";
import ApiService from "@/services/ApiService";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Frame,
  Panel,
  Feedback,
  LoadState,
  useData,
  useMutation,
  fieldClass,
} from "./ui";
type Organization = {
  id: number;
  name?: string;
  name_eng: string;
  name_jp: string;
  postal_code: string;
  state_id: number;
  address: string;
  website: string;
  phone: string;
  status: string;
  status_reason?: string;
  founded_year?: string;
  employee_numbers?: number;
  remark?: string;
};
const empty = {
  name_eng: "",
  name_jp: "",
  postal_code: "",
  state_id: 0,
  address: "",
  website: "",
  phone: "",
  founded_year: "",
  employee_numbers: 0,
  remark: "",
};
export default function OrganizationPage() {
  const { session, tenant, can } = useAdminSession();
  const { type, id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const creating = location.pathname.endsWith("/new");
  const platform = location.pathname.startsWith("/platform");
  const [kind, setKind] = useState(type ?? "company");
  const result = useData<{
    organization: Organization;
    staff_count: number;
    jobs_count: number;
  }>(
    creating
      ? "platform/overview"
      : platform
        ? "platform/organizations/" + type + "/" + id
        : "organization",
  );
  const regions = useData<{ id: number; name: string }[]>(
    platform ? "platform/regions" : "states",
  );
  const [form, setForm] = useState(empty);
  const [owner, setOwner] = useState("");
  const [status, setStatus] = useState("pending");
  const [reason, setReason] = useState("");
  const op = useMutation();
  useEffect(() => {
    if (!creating && result.data?.organization) {
      const o = result.data.organization;
      setForm({
        ...empty,
        ...o,
        name_eng: o.name_eng || o.name || "",
        name_jp: o.name_jp || "",
        postal_code: o.postal_code || "",
        website: o.website || "",
        remark: o.remark || "",
        founded_year: o.founded_year || "",
      });
      setStatus(o.status);
    }
  }, [result.data, creating]);
  const editable = platform
    ? can("platform.organizations.manage")
    : can("organization.manage");
  const save = async (e: FormEvent) => {
    e.preventDefault();
    await op.run(async () => {
      let ownerId: number | undefined;
      if (creating && owner)
        ownerId = (
          await ApiService.get("platform/users/lookup", {
            params: { email: owner },
          })
        ).data.data.id;
      const body = {
        ...form,
        state_id: Number(form.state_id),
        employee_numbers: Number(form.employee_numbers),
        website: form.website || null,
        founded_year: form.founded_year ? Number(form.founded_year) : null,
      };
      if (creating) {
        const r = await ApiService.post("platform/organizations", {
          ...body,
          type: kind,
          owner_user_id: ownerId,
        });
        navigate(
          "/platform/organizations/" + kind + "/" + r.data.data.organization.id,
        );
      } else {
        await ApiService.patch(
          platform
            ? "platform/organizations/" + type + "/" + id
            : "organization",
          body,
        );
        result.reload();
      }
    });
  };
  return (
    <Frame
      title={creating ? "組織を登録" : platform ? "組織の管理" : "組織情報"}
      description={
        creating
          ? "登録後に利用状態を「有効」にして、スタッフを招待します。"
          : undefined
      }
    >
      <Feedback {...op} />
      {!creating && <LoadState {...result} />}
      {(creating || result.data?.organization) && (
        <Panel title={result.data?.organization?.name_jp}>
          <form onSubmit={save} className="grid sm:grid-cols-2 gap-4">
            {creating && (
              <>
                <label>
                  種類
                  <select
                    className={fieldClass}
                    value={kind}
                    onChange={(e) => setKind(e.target.value)}
                  >
                    <option value="company">企業</option>
                    <option value="agent">紹介会社</option>
                  </select>
                </label>
                <label>
                  管理者のOOAKアカウント（メール）
                  <Input
                    required={kind === "agent"}
                    type="email"
                    value={owner}
                    onChange={(e) => setOwner(e.target.value)}
                  />
                </label>
              </>
            )}
            <label>
              名称（英語）
              <Input
                required
                disabled={!editable}
                value={form.name_eng}
                onChange={(e) => setForm({ ...form, name_eng: e.target.value })}
              />
            </label>
            <label>
              名称（日本語）
              <Input
                required
                disabled={!editable}
                value={form.name_jp}
                onChange={(e) => setForm({ ...form, name_jp: e.target.value })}
              />
            </label>
            <label>
              郵便番号
              <Input
                disabled={!editable}
                value={form.postal_code}
                onChange={(e) =>
                  setForm({ ...form, postal_code: e.target.value })
                }
              />
            </label>
            <label>
              都道府県
              <select
                required
                disabled={!editable}
                className={fieldClass}
                value={form.state_id}
                onChange={(e) =>
                  setForm({ ...form, state_id: Number(e.target.value) })
                }
              >
                <option value={0}>選択してください</option>
                {regions.data?.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="sm:col-span-2">
              所在地
              <Input
                required
                disabled={!editable}
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
              />
            </label>
            <label>
              電話
              <Input
                required
                disabled={!editable}
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
              />
            </label>
            <label>
              Webサイト
              <Input
                type="url"
                disabled={!editable}
                value={form.website}
                onChange={(e) => setForm({ ...form, website: e.target.value })}
              />
            </label>
            {(creating ? kind : platform ? type : tenant?.type) === "company" && (
              <>
                <label>
                  設立年
                  <Input
                    type="number"
                    min={1000}
                    max={2100}
                    disabled={!editable}
                    value={form.founded_year}
                    onChange={(e) =>
                      setForm({ ...form, founded_year: e.target.value })
                    }
                  />
                </label>
                <label>
                  従業員数
                  <Input
                    type="number"
                    min={0}
                    disabled={!editable}
                    value={form.employee_numbers}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        employee_numbers: Number(e.target.value),
                      })
                    }
                  />
                </label>
                <label className="sm:col-span-2">
                  備考
                  <textarea
                    className={fieldClass}
                    disabled={!editable}
                    value={form.remark}
                    onChange={(e) =>
                      setForm({ ...form, remark: e.target.value })
                    }
                  />
                </label>
              </>
            )}
            {editable && (
              <Button disabled={op.busy || !form.state_id}>
                {creating ? "登録" : "保存"}
              </Button>
            )}
          </form>
          <Feedback error={regions.error} />
          {!creating && (
            <p className="text-sm text-muted-foreground">
              求人 {result.data?.jobs_count}件 / スタッフ{" "}
              {result.data?.staff_count}名 / 利用状態{" "}
              {result.data?.organization.status}
            </p>
          )}
        </Panel>
      )}
      {platform && !creating && result.data?.organization && (
        <Panel title="利用状態">
          {editable && (
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                await op.run(async () => {
                  await ApiService.post(
                    "platform/organizations/" + type + "/" + id + "/status",
                    { status, reason },
                  );
                  result.reload();
                }, "利用状態を変更しました。");
              }}
              className="space-y-4"
            >
              <label className="block">
                状態
                <select
                  className={fieldClass}
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <option value="pending">承認待ち</option>
                  <option value="active">有効</option>
                  <option value="suspended">停止</option>
                  <option value="archived">アーカイブ</option>
                </select>
              </label>
              <label className="block">
                変更理由
                <Input
                  required
                  maxLength={500}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
              </label>
              <Button disabled={op.busy}>状態を変更</Button>
            </form>
          )}
          {result.data.organization.status === "active" && (
            <Button
              variant="outline"
              onClick={() =>
                openTenant(
                  {
                    type: type as "agent" | "company",
                    id: Number(id),
                    name:
                      result.data.organization.name_jp ||
                      result.data.organization.name_eng,
                    actorId: session.user.id,
                  },
                  "/organization",
                )
              }
            >
              この組織の管理画面へ
            </Button>
          )}
        </Panel>
      )}
    </Frame>
  );
}
